import { useTranslation } from "react-i18next";

import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/features/consent/ConsentProvider";

import { POLICY_VERSION } from "@/features/consent/consent-config";

/**
 * SPEC-COOKIE-5 — final KVKK aydınlatma metni + Çerez Politikası page.
 *
 * One page, two sections (the original DRAFT scaffold + every
 * "[Hukuk incelemesi bekleniyor]" marker have been removed):
 *   1. 6698 Sayılı KVKK Aydınlatma Metni (subsections 1-6)
 *   2. Çerez (Cookie) Politikası (subsections 1-2)
 *
 * Locale-driven. Turkish copy is rendered verbatim from `tr.json`'s
 * `consent.policy.*` keys (sourced from frontend-specs/prompts/
 * kvkk-aydinlatma-metni.md + cerez-politikasi.md); English copy is a
 * faithful translation in `en.json`.
 *
 * SPEC-COOKIE-5 Part A — the COOKIE-4 blocking overlay is suppressed on
 * this route by `CookieConsentBanner.tsx` so the page is fully readable
 * and scrollable while a decision is still pending. The banner bar
 * remains at the bottom so the visitor can decide from this page.
 */
export function CookiePolicyPage() {
  const { t } = useTranslation();
  const { openManager } = useConsent();

  return (
    <PageContainer variant="reading" data-testid="cookie-policy-page">
      <article className="space-y-8 py-6 text-sm leading-relaxed">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("consent.policy.title")}
          </h1>
          <p className="text-xs text-muted-foreground">
            {t("consent.policy.version", { version: POLICY_VERSION })}
          </p>
        </header>

        {/* ─── Section 1 — KVKK Aydınlatma Metni ────────────────────── */}
        <section className="space-y-6">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("consent.policy.section1.title")}
          </h2>

          {/* 1. Veri Sorumlusunun Kimliği */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s1.title")}
            </h3>
            <p>{t("consent.policy.section1.s1.body")}</p>
          </section>

          {/* 2. İşlenen Kişisel Verileriniz */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s2.title")}
            </h3>
            <p>{t("consent.policy.section1.s2.intro")}</p>
            <p>
              <strong>{t("consent.policy.section1.s2.identityLabel")}</strong>{" "}
              {t("consent.policy.section1.s2.identityBody")}
            </p>
            <p>
              <strong>
                {t("consent.policy.section1.s2.transactionLabel")}
              </strong>{" "}
              {t("consent.policy.section1.s2.transactionBody")}
            </p>
            <p>
              <strong>{t("consent.policy.section1.s2.securityLabel")}</strong>{" "}
              {t("consent.policy.section1.s2.securityBody")}
            </p>
          </section>

          {/* 3. Kişisel Verilerin İşlenme Amaçları ve Hukuki Sebepleri */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s3.title")}
            </h3>
            <p>{t("consent.policy.section1.s3.intro")}</p>
            <p>
              {t("consent.policy.section1.s3.p1")}
              <strong>{t("consent.policy.section1.s3.p1Cite")}</strong>
              {t("consent.policy.section1.s3.p1Tail")}
            </p>
            <p>
              {t("consent.policy.section1.s3.p2")}
              <strong>{t("consent.policy.section1.s3.p2Cite")}</strong>
              {t("consent.policy.section1.s3.p2Tail")}
            </p>
            <p>
              {t("consent.policy.section1.s3.p3")}
              <strong>{t("consent.policy.section1.s3.p3Cite")}</strong>
              {t("consent.policy.section1.s3.p3Tail")}
            </p>
          </section>

          {/* 4. Kişisel Verilerin Aktarılması (KVKK Madde 9) */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s4.title")}
            </h3>
            <p>{t("consent.policy.section1.s4.noSale")}</p>
            <p>{t("consent.policy.section1.s4.intro")}</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>
                  {t("consent.policy.section1.s4.supabaseLabel")}
                </strong>{" "}
                {t("consent.policy.section1.s4.supabaseBody")}
              </li>
              <li>
                <strong>
                  {t("consent.policy.section1.s4.railwayLabel")}
                </strong>{" "}
                {t("consent.policy.section1.s4.railwayBody")}
              </li>
              <li>
                <strong>
                  {t("consent.policy.section1.s4.cloudflareLabel")}
                </strong>{" "}
                {t("consent.policy.section1.s4.cloudflareBody")}
              </li>
            </ul>
            <p>{t("consent.policy.section1.s4.platforms")}</p>
            <p>{t("consent.policy.section1.s4.tail")}</p>
            <p>{t("consent.policy.section1.s4.legal")}</p>
          </section>

          {/* 5. Veri Güvenliği ve Saklama Süreleri */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s5.title")}
            </h3>
            <p>{t("consent.policy.section1.s5.body")}</p>
          </section>

          {/* 6. Veri Sahibinin Hakları (KVKK Madde 11) */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section1.s6.title")}
            </h3>
            <p>{t("consent.policy.section1.s6.body")}</p>
            <p>{t("consent.policy.section1.s6.contact")}</p>
          </section>
        </section>

        {/* ─── Section 2 — Çerez (Cookie) Politikası ────────────────── */}
        <section className="space-y-6">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("consent.policy.section2.title")}
          </h2>
          <p>{t("consent.policy.section2.intro")}</p>

          {/* 1. Kullanılan Çerez Türleri ve Amaçları */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section2.s1.title")}
            </h3>
            <p>{t("consent.policy.section2.s1.intro")}</p>
            <p>
              <strong>
                {t("consent.policy.section2.s1.necessaryLabel")}
              </strong>{" "}
              {t("consent.policy.section2.s1.necessaryBody")}
            </p>
            <p>
              <strong>
                {t("consent.policy.section2.s1.analyticsLabel")}
              </strong>{" "}
              {t("consent.policy.section2.s1.analyticsBody")}
            </p>
          </section>

          {/* 2. Çerezlerin Yönetimi */}
          <section className="space-y-2">
            <h3 className="text-lg font-semibold">
              {t("consent.policy.section2.s2.title")}
            </h3>
            <p>{t("consent.policy.section2.s2.body")}</p>
            <Button
              type="button"
              onClick={openManager}
              data-testid="cookie-policy-open-manager"
              className="mt-2"
            >
              {t("consent.policy.manageButton")}
            </Button>
          </section>
        </section>
      </article>
    </PageContainer>
  );
}
